import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth"
import { Hub } from "aws-amplify/utils"

// The sign-in method last used on this device, so the sign-in screen can mark
// its button and a wrong-password error can point SSO users to it. Storage can
// be blocked (private mode, site data policy): every access is best effort.
export type SignInMethod = "password" | "google" | "microsoft"

const LAST_METHOD_KEY = "turnus.lastSignInMethod"
// A sign-in that does not end in handleSignIn: Google leaves the page
// (signInWithRedirect), and a password sign-in with a next step (new password,
// MFA, sign-up confirmation with auto sign-in) completes inside the
// Authenticator. The method is recorded once the app is authenticated.
const PENDING_METHOD_KEY = "turnus.pendingSignInMethod"

type PendingSignInMethod = Exclude<SignInMethod, "microsoft">

const SIGN_IN_METHODS: readonly SignInMethod[] = [
  "password",
  "google",
  "microsoft",
]

const isSignInMethod = (value: unknown): value is SignInMethod =>
  SIGN_IN_METHODS.includes(value as SignInMethod)

export function readLastSignInMethod(): SignInMethod | null {
  try {
    const value = localStorage.getItem(LAST_METHOD_KEY)
    return isSignInMethod(value) ? value : null
  } catch {
    return null
  }
}

function readPendingSignInMethod(): PendingSignInMethod | null {
  try {
    const value = sessionStorage.getItem(PENDING_METHOD_KEY)
    return value === "password" || value === "google" ? value : null
  } catch {
    return null
  }
}

function clearPendingSignInMethod(): void {
  try {
    sessionStorage.removeItem(PENDING_METHOD_KEY)
  } catch {
    // Storage blocked: there is no marker to clear either.
  }
}

export function recordSignInMethod(method: SignInMethod): void {
  clearPendingSignInMethod()
  try {
    localStorage.setItem(LAST_METHOD_KEY, method)
  } catch {
    // Storage blocked: the badge and the SSO hint just stay generic.
  }
}

export function markSignInPending(method: PendingSignInMethod): void {
  try {
    sessionStorage.setItem(PENDING_METHOD_KEY, method)
  } catch {
    // Storage blocked: this sign-in is not remembered.
  }
}

/** Drops a pending Google sign-in whose redirect failed. */
export function clearPendingGoogleSignIn(): void {
  if (readPendingSignInMethod() === "google") clearPendingSignInMethod()
}

// Cognito puts the federated provider into the ID token's `identities` claim
// (an array, sometimes serialised as a JSON string).
function hasGoogleIdentity(identities: unknown): boolean {
  let parsed = identities
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed)
    } catch {
      return false
    }
  }
  return (
    Array.isArray(parsed) &&
    parsed.some(
      (identity) =>
        typeof identity === "object" &&
        identity !== null &&
        (identity as { providerName?: unknown }).providerName === "Google",
    )
  )
}

// Amplify keeps the flow of the sign-in that created the tokens. The
// Microsoft sign-in uses CUSTOM_WITHOUT_SRP and records itself; a Google
// (OAuth) session has none.
const PASSWORD_AUTH_FLOWS: readonly string[] = [
  "USER_SRP_AUTH",
  "USER_PASSWORD_AUTH",
]

/** Call once the app is authenticated. A pending marker only says that a
 *  sign-in started in this tab may have finished without being recorded;
 *  the method is taken from the session itself, so a stale or overwritten
 *  marker (abandoned attempt, another tab) is dropped instead of guessed. */
export async function promotePendingSignIn(): Promise<void> {
  if (readPendingSignInMethod() === null) return
  try {
    const [session, user] = await Promise.all([
      fetchAuthSession(),
      getCurrentUser(),
    ])
    // Another sign-in may have been recorded while the session was read.
    if (readPendingSignInMethod() === null) return
    if (hasGoogleIdentity(session.tokens?.idToken?.payload.identities)) {
      recordSignInMethod("google")
      return
    }
    if (PASSWORD_AUTH_FLOWS.includes(user.signInDetails?.authFlowType ?? "")) {
      recordSignInMethod("password")
      return
    }
  } catch {
    // No readable session: do not guess.
  }
  clearPendingSignInMethod()
}

/** Drops a pending Google sign-in when Amplify reports a failed redirect
 *  return. */
export function listenForGoogleSignInFailure(): () => void {
  return Hub.listen("auth", ({ payload }) => {
    if (payload.event === "signInWithRedirect_failure") {
      clearPendingGoogleSignIn()
    }
  })
}
